import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import './GameSearchBar.css'

export default function GameSearchBar({ onResults, autoFocus = false, initialQuery = '' }) {
  const [query, setQuery] = useState(initialQuery)
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [showResults, setShowResults] = useState(false)
  const navigate = useNavigate()

  // Update query when initialQuery changes (e.g., from URL parameter)
  useEffect(() => {
    if (initialQuery && initialQuery !== query) {
      setQuery(initialQuery)
    }
  }, [initialQuery])

  useEffect(() => {
    if (query.length < 2) {
      setResults([])
      setShowResults(false)
      return
    }

    const timeoutId = setTimeout(async () => {
      setLoading(true)
      try {
        const response = await axios.get(`/api/games/search?q=${encodeURIComponent(query)}`)
        setResults(response.data.results || [])
        setShowResults(true)
        if (onResults) {
          onResults(response.data.results || [])
        }
      } catch (error) {
        console.error('Search error:', error)
        setResults([])
      } finally {
        setLoading(false)
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [query, onResults])

  const handleGameClick = (game) => {
    navigate(`/game/${game.id}`)
    setQuery('')
    setShowResults(false)
  }

  return (
    <div className="game-search">
      <div className="search-input-container">
        <span className="search-icon">🔍</span>
        <input
          type="text"
          className="search-input"
          placeholder="Search for a game..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.length >= 2 && setShowResults(true)}
          autoFocus={autoFocus}
        />
        {loading && <span className="search-loading">⏳</span>}
        {query && (
          <button
            className="search-clear"
            onClick={() => {
              setQuery('')
              setResults([])
              setShowResults(false)
            }}
          >
            ✕
          </button>
        )}
      </div>

      {showResults && results.length > 0 && (
        <div className="search-results">
          {results.map((game, index) => (
            <div
              key={game.id}
              className="search-result-item fade-in"
              style={{ animationDelay: `${index * 50}ms` }}
              onClick={() => handleGameClick(game)}
            >
              {game.background_image && (
                <img
                  src={game.background_image}
                  alt={game.name}
                  className="result-image"
                />
              )}
              <div className="result-info">
                <div className="result-name">{game.name}</div>
                <div className="result-meta">
                  {game.released && <span>{new Date(game.released).getFullYear()}</span>}
                  {game.platforms && game.platforms.length > 0 && (
                    <span>{game.platforms[0].platform.name}</span>
                  )}
                  {game.rating && <span>⭐ {game.rating}/5</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showResults && query.length >= 2 && results.length === 0 && !loading && (
        <div className="search-no-results">
          No games found for "{query}"
        </div>
      )}
    </div>
  )
}
